import { PlansScreen } from '../../../../../analytics/plansEvents'
import { useRef } from 'react'
import type { LumpsumAllocationBuyPlanRecommendationDetails } from '../../../api/allocationRecommendation.types'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import type { FundMissedGainsInfoParams } from '../../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { buildAllocationBuyRecommendationPair } from '../../../pair/buildPlanRecommendationPair'
import {
  PlanTaxLiability,
  areAllSellActionsCompleted
} from '../../../shared/tax/PlanTaxLiability'
import { ReasonsToBuyAccordion } from '../../../shared/reasons/ReasonsToBuyAccordion'
import { ReasonsToSellAccordion } from '../../../shared/reasons/ReasonsToSellAccordion'
import { StepsToRebalanceAccordion, StepsAccordionPlanType } from '../../../shared/steps/StepsToRebalanceAccordion'
import { PlanUnderlineTabs } from '../../../shared/sheet/PlanUnderlineTabs'
import { useScrollToFirstAccordionOnTabChange } from '../../../shared/sheet/planRecommendationSheetScroll'

type AllocationBuyPlanRecommendationBodyProps = {
  planDetails: LumpsumAllocationBuyPlanRecommendationDetails | null
  selectedSellIndex: number
  onSelectSellIndex: (selectedIndex: number) => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

export function AllocationBuyPlanRecommendationBody({
  planDetails,
  selectedSellIndex,
  onSelectSellIndex,
  onMissedGainsInfoClick
}: AllocationBuyPlanRecommendationBodyProps) {
  const sellFunds = planDetails?.fundsToBeSold ?? []
  const stickyTabsRef = useRef<HTMLDivElement>(null)
  const firstAccordionRef = useRef<HTMLDivElement>(null)
  useScrollToFirstAccordionOnTabChange(stickyTabsRef, firstAccordionRef, selectedSellIndex)

  const isTaxOutside = sellFunds.length <= 1
  const outsideTaxData = planDetails?.consolidatedTax ?? sellFunds[0]?.taxData ?? null
  const areAllSellsCompleted = areAllSellActionsCompleted(sellFunds)
  // 1:1 → root side; 1:m → counter (sell) side
  const detailsTitle = sellFunds.length > 1 ? 'Sell details' : 'Buy details'

  return (
    <>
      <div className="h-[10px] bg-[#F0F4F7] mt-6" />

      <div ref={stickyTabsRef} className="sticky top-0 z-[1] bg-white pt-5">
        <div className="px-5 pb-6 pt-2 text-[18px] font-[500] leading-[130%] text-[#232323]">
          {detailsTitle}
        </div>
        <PlanUnderlineTabs
          analyticsScreen={PlansScreen.LumpsumAllocationRecommendation}
          funds={sellFunds.map((fund) => ({
            fundName: fund?.fundName ?? '',
            fundIsin: fund?.isin ?? '',
            mfLogoUrl: fund?.mfLogoUrl ?? ''
          }))}
          activeTabIndex={selectedSellIndex}
          onChange={onSelectSellIndex}
        />
      </div>

      <div ref={firstAccordionRef} aria-hidden />

      {sellFunds.map((sellFund, sellFundIndex) => {
        const pair = buildAllocationBuyRecommendationPair(planDetails, sellFund)
        if (!pair) {
          return null
        }

        return (
          <div
            key={sellFund?.isin ?? String(sellFundIndex)}
            className={sellFundIndex === selectedSellIndex ? undefined : 'hidden'}
            aria-hidden={sellFundIndex !== selectedSellIndex}
          >
            <ReasonsToBuyAccordion pair={pair} />
            <ReasonsToSellAccordion
              pair={pair}
              onMissedGainsInfoClick={onMissedGainsInfoClick}
            />
            <StepsToRebalanceAccordion
              stepsPlanType={StepsAccordionPlanType.REALLOCATE}
              pair={pair}
            />
            {!isTaxOutside ? (
              <PlanTaxLiability
                isOutside={false}
                areAllSellActionsCompleted={areAllSellsCompleted}
                fundName={pair?.exitingFund?.fundName ?? ''}
                fundIsin={pair?.exitingFund?.isin ?? ''}
                tax={{
                  taxData: pair.exitingFund.taxData,
                  withdrawalAmount: pair.exitingFund.sellAmount ?? 0,
                  currentValue: pair.exitingFund.taxData?.currentValue ?? null
                }}
              />
            ) : null}
          </div>
        )
      })}

      {isTaxOutside ? (
        <PlanTaxLiability
          isOutside
          areAllSellActionsCompleted={areAllSellsCompleted}
          fundName={sellFunds[0]?.fundName ?? ''}
          fundIsin={sellFunds[0]?.isin ?? ''}
          tax={{
            taxData: outsideTaxData,
            // Redeem from the sell tab — not root totalTransferAmount (buy-side).
            withdrawalAmount: showAsFormattedRupeeAmount({
              rawAmount: sellFunds[0]?.sellAmount ?? 0
            }).value,
            currentValue: outsideTaxData?.currentValue ?? null
          }}
        />
      ) : null}
    </>
  )
}
